/**
 * Ancarta Stockists — email gate + sample/application/event-support forms.
 * Isolated to /stockists. Never imports, reads, or writes anything Brand
 * Rep/Giveaway/newsletter related — see ancarta-brand-rep-backend/src/
 * stockist.js for the matching backend isolation.
 *
 * LOCAL STORAGE: stores only the business email she typed, under
 * `ancartaStockistEmail` — no token, no password, no session secret. This
 * is a lead-capture gate, not a security boundary (see the backend's own
 * doc comment), so a plain saved email is sufficient to skip re-typing it
 * on a return visit; nothing sensitive is ever written to browser storage.
 */

const STORAGE_KEY = 'ancartaStockistEmail';
// Harmless, non-sensitive business fields only (name/type/phone/etc — never
// samples, never pricing, never anything server-authoritative) — purely so
// a prospect who already filled in the sample form isn't asked to
// re-type the same business details on the application form below.
const BUSINESS_DETAILS_KEY = 'ancartaStockistBusinessDetails';
const SOURCE_KEY = 'ancartaStockistSource';
const VALID_SOURCES = ['email', 'instagram', 'tiktok', 'ruby', 'perth_outreach'];
const MAX_SAMPLES = 3;

function publish(eventName, payload) {
  try {
    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish(eventName, payload || {});
    }
  } catch (error) {
    /* analytics must never break the funnel */
  }
}

function generateIdempotencyKey() {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
  } catch (error) {
    /* fall through */
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function captureSource() {
  try {
    const existing = sessionStorage.getItem(SOURCE_KEY);
    if (existing) return existing;
  } catch (error) {
    /* sessionStorage may be unavailable */
  }
  const params = new URLSearchParams(window.location.search);
  let source = (params.get('source') || params.get('utm_source') || '').toLowerCase();
  if (!VALID_SOURCES.includes(source)) source = 'direct';
  try {
    sessionStorage.setItem(SOURCE_KEY, source);
  } catch (error) {
    /* non-fatal */
  }
  return source;
}

function getSavedEmail() {
  try {
    return localStorage.getItem(STORAGE_KEY) || '';
  } catch (error) {
    return '';
  }
}

function saveEmail(email) {
  try {
    localStorage.setItem(STORAGE_KEY, email);
  } catch (error) {
    /* private mode / storage disabled — non-fatal, just re-asks next visit */
  }
}

function clearSavedEmail() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    /* non-fatal */
  }
}

/** Caches business fields (not samples, not pricing) once she's filled the sample form, so the application form below can prefill instead of asking twice. */
function saveBusinessDetails(fields) {
  try {
    localStorage.setItem(BUSINESS_DETAILS_KEY, JSON.stringify(fields));
  } catch (error) {
    /* private mode / storage disabled — non-fatal, just re-asks on the application form */
  }
}

function getBusinessDetails() {
  try {
    return JSON.parse(localStorage.getItem(BUSINESS_DETAILS_KEY) || '{}');
  } catch (error) {
    return {};
  }
}

function revealGatedContent() {
  document.querySelectorAll('[data-stockist-gated]').forEach((el) => {
    el.hidden = false;
  });
}

function hideGatedContent() {
  document.querySelectorAll('[data-stockist-gated]').forEach((el) => {
    el.hidden = true;
  });
}

async function postJson(apiBase, path, body, { idempotencyKey } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
  const response = await fetch(apiBase.replace(/\/$/, '') + path, { method: 'POST', headers, body: JSON.stringify(body) });
  let data = null;
  try {
    data = await response.json();
  } catch (error) {
    /* non-JSON error response — data stays null, handled by caller */
  }
  return { response, data };
}

function initGate() {
  const gate = document.querySelector('.stk-gate');
  if (!gate) return;

  const apiBase = gate.getAttribute('data-api-base') || '';
  const gateForm = gate.querySelector('[data-ref="gateForm"]');
  const gateFormEl = gate.querySelector('[data-ref="gateFormEl"]');
  const emailInput = gate.querySelector('[data-ref="emailInput"]');
  const submitButton = gate.querySelector('[data-ref="gateSubmit"]');
  const submitLabel = gate.querySelector('[data-ref="gateSubmitLabel"]');
  const errorEl = gate.querySelector('[data-ref="gateError"]');
  const statusLive = gate.querySelector('[data-ref="gateStatusLive"]');
  const unlockedPanel = gate.querySelector('[data-ref="gateUnlocked"]');
  const unlockedEmail = gate.querySelector('[data-ref="gateUnlockedEmail"]');
  const resetButton = gate.querySelector('[data-ref="gateReset"]');

  publish('stockist_page_view', {});

  let gateStarted = false;
  if (emailInput) {
    emailInput.addEventListener('input', () => {
      if (!gateStarted) {
        gateStarted = true;
        publish('stockist_email_gate_started', {});
      }
    });
  }

  function showUnlocked(email) {
    if (gateForm) gateForm.hidden = true;
    if (unlockedPanel) unlockedPanel.hidden = false;
    if (unlockedEmail) unlockedEmail.textContent = email ? ' (' + email + ')' : '';
    revealGatedContent();
  }

  function showGateForm() {
    if (gateForm) gateForm.hidden = false;
    if (unlockedPanel) unlockedPanel.hidden = true;
    hideGatedContent();
  }

  const savedEmail = getSavedEmail();
  if (savedEmail && apiBase) {
    showUnlocked(savedEmail);
    // Fire-and-forget: keeps lastAccessedAt fresh on a return visit without
    // making her wait on it or re-type anything.
    postJson(apiBase, '/stockists/access', { email: savedEmail, source: captureSource() }).catch(() => {});
  }

  if (resetButton) {
    resetButton.addEventListener('click', () => {
      clearSavedEmail();
      showGateForm();
      if (emailInput) emailInput.value = '';
    });
  }

  if (!gateFormEl) return;

  gateFormEl.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!apiBase) {
      if (errorEl) {
        errorEl.textContent = 'Stockist access is not configured yet — check back soon.';
        errorEl.hidden = false;
      }
      return;
    }

    const email = (emailInput.value || '').trim();
    if (!email) return;

    submitButton.disabled = true;
    if (submitLabel) submitLabel.textContent = 'CHECKING…';
    if (errorEl) errorEl.hidden = true;
    if (statusLive) statusLive.textContent = 'Checking your email…';

    let result;
    try {
      result = await postJson(apiBase, '/stockists/access', { email, source: captureSource() });
    } catch (networkError) {
      submitButton.disabled = false;
      if (submitLabel) submitLabel.textContent = 'VIEW STOCKIST INFORMATION';
      if (errorEl) {
        errorEl.textContent = "We couldn't reach Ancarta right now. Check your connection and try again.";
        errorEl.hidden = false;
      }
      return;
    }

    submitButton.disabled = false;
    if (submitLabel) submitLabel.textContent = 'VIEW STOCKIST INFORMATION';

    if (result.response.ok && result.data && result.data.success) {
      saveEmail(email);
      showUnlocked(email);
      publish('stockist_email_captured', {});
      return;
    }

    if (errorEl) {
      errorEl.textContent = result.data && result.data.error === 'invalid_email'
        ? 'Enter a valid business email address.'
        : "Something went wrong. Please try again.";
      errorEl.hidden = false;
    }
  });
}

function initPricingViewedTracking(apiBase) {
  const pricingSection = document.getElementById('stockist-economics');
  if (!pricingSection || !('IntersectionObserver' in window)) return;
  let fired = false;
  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting && !fired) {
        fired = true;
        publish('stockist_pricing_viewed', {});
        const email = getSavedEmail();
        if (email && apiBase) {
          postJson(apiBase, '/stockists/pricing-viewed', { email }).catch(() => {});
        }
        observer.disconnect();
      }
    }
  }, { threshold: 0.4 });
  observer.observe(pricingSection);
}

function initStartingEditTracking() {
  const grid = document.querySelector('.stk-edit-grid');
  if (!grid || !('IntersectionObserver' in window)) return;
  let fired = false;
  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting && !fired) {
        fired = true;
        publish('stockist_starting_edit_viewed', {});
        observer.disconnect();
      }
    }
  }, { threshold: 0.3 });
  observer.observe(grid);
}

function collectFormFields(form) {
  const fields = {};
  form.querySelectorAll('[data-field]').forEach((input) => {
    fields[input.getAttribute('data-field')] = input.value ? input.value.trim() : '';
  });
  return fields;
}

function setFormLoading(submitButton, labelEl, loadingText, idleText, loading) {
  if (submitButton) submitButton.disabled = loading;
  if (labelEl) labelEl.textContent = loading ? loadingText : idleText;
}

function showFormMessage(el, message) {
  if (!el) return;
  el.textContent = message;
  el.hidden = false;
}

function hideFormMessage(el) {
  if (!el) return;
  el.hidden = true;
  el.textContent = '';
}

/** Sample picker: cascading PRODUCT -> SIZE selects, capped at MAX_SAMPLES slots. */
function initSamplePicker(apiBase) {
  const openButton = document.querySelector('[data-ref="sampleOpenButton"]');
  const picker = document.querySelector('[data-ref="samplePicker"]');
  if (!openButton || !picker) return;

  const catalogScript = picker.querySelector('[data-ref="sampleCatalogData"]');
  let catalog = [];
  try {
    catalog = JSON.parse(catalogScript.textContent || '[]');
  } catch (error) {
    catalog = [];
  }

  const slotsContainer = picker.querySelector('[data-ref="sampleSlots"]');
  const addSlotButton = picker.querySelector('[data-ref="sampleAddSlot"]');
  const form = picker.querySelector('[data-ref="sampleForm"]');
  const submitButton = picker.querySelector('[data-ref="sampleSubmit"]');
  const submitLabel = picker.querySelector('[data-ref="sampleSubmitLabel"]');
  const errorEl = picker.querySelector('[data-ref="sampleError"]');
  const successEl = picker.querySelector('[data-ref="sampleSuccess"]');

  let slotCount = 0;
  let opened = false;

  function updateAddButton() {
    if (addSlotButton) addSlotButton.hidden = slotCount >= MAX_SAMPLES;
  }

  function renumberSlots() {
    slotsContainer.querySelectorAll('.stk-sample-slot').forEach((slot, index) => {
      const label = slot.querySelector('.stk-sample-slot__label');
      if (label) label.textContent = 'PIECE ' + (index + 1);
    });
  }

  function addSlot() {
    if (slotCount >= MAX_SAMPLES || !catalog.length) return;
    slotCount++;

    const slot = document.createElement('div');
    slot.className = 'stk-sample-slot';

    const label = document.createElement('p');
    label.className = 'stk-sample-slot__label';
    label.textContent = 'PIECE ' + slotCount;
    slot.appendChild(label);

    const productSelect = document.createElement('select');
    productSelect.setAttribute('data-slot-product', '');
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = 'SELECT STYLE / PRODUCT';
    productSelect.appendChild(placeholder);

    const byCollection = {};
    catalog.forEach((product) => {
      if (!byCollection[product.collection]) byCollection[product.collection] = [];
      byCollection[product.collection].push(product);
    });
    Object.entries(byCollection).forEach(([collectionName, products]) => {
      const group = document.createElement('optgroup');
      group.label = collectionName;
      products.forEach((product) => {
        const option = document.createElement('option');
        option.value = product.productId;
        option.textContent = product.title;
        group.appendChild(option);
      });
      productSelect.appendChild(group);
    });

    const sizeSelect = document.createElement('select');
    sizeSelect.setAttribute('data-slot-size', '');
    sizeSelect.disabled = true;
    const sizePlaceholder = document.createElement('option');
    sizePlaceholder.value = '';
    sizePlaceholder.textContent = 'SELECT SIZE';
    sizeSelect.appendChild(sizePlaceholder);

    productSelect.addEventListener('change', () => {
      sizeSelect.innerHTML = '';
      sizeSelect.appendChild(sizePlaceholder.cloneNode(true));
      // product.id renders from Liquid as a JSON number, but a <select>'s
      // .value is always a string — compare as strings on both sides
      // rather than relying on productId already being a string.
      const product = catalog.find((p) => String(p.productId) === productSelect.value);
      if (product) {
        sizeSelect.disabled = false;
        product.variants.forEach((variant) => {
          const option = document.createElement('option');
          option.value = variant.gid;
          option.textContent = variant.size || variant.id;
          sizeSelect.appendChild(option);
        });
      } else {
        sizeSelect.disabled = true;
      }
    });

    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.className = 'stk-sample-slot__remove';
    removeButton.textContent = 'REMOVE';
    removeButton.addEventListener('click', () => {
      slot.remove();
      slotCount--;
      updateAddButton();
      renumberSlots();
    });

    slot.appendChild(productSelect);
    slot.appendChild(sizeSelect);
    slot.appendChild(removeButton);
    slotsContainer.appendChild(slot);
    updateAddButton();
  }

  // Generated once when the sample form is first shown, not per submit
  // attempt — matches assets/brand-rep.js's own idempotency discipline (see
  // its generateIdempotencyKey doc comment): reusing the same key across a
  // network-error retry or an accidental double-click is exactly what lets
  // the backend's Idempotency-Key cache recognise a replay and hand back
  // the SAME draft order/checkout URL instead of minting a second one. Only
  // regenerated after a definitive response (success or a real rejection
  // like ineligible_samples) is received, when the next attempt is
  // genuinely a new one.
  let sampleIdempotencyKey = null;

  openButton.addEventListener('click', () => {
    picker.hidden = false;
    openButton.hidden = true;
    if (!opened) {
      opened = true;
      sampleIdempotencyKey = generateIdempotencyKey();
      publish('stockist_sample_started', {});
      addSlot();
    }
  });

  if (addSlotButton) addSlotButton.addEventListener('click', addSlot);

  if (form) {
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      hideFormMessage(errorEl);
      hideFormMessage(successEl);

      const email = getSavedEmail();
      if (!email || !apiBase) return;

      const samples = [];
      slotsContainer.querySelectorAll('.stk-sample-slot').forEach((slot) => {
        const sizeSelect = slot.querySelector('[data-slot-size]');
        if (sizeSelect && sizeSelect.value) samples.push({ variantId: sizeSelect.value });
      });

      if (samples.length === 0) {
        showFormMessage(errorEl, 'Choose at least one piece above.');
        return;
      }

      setFormLoading(submitButton, submitLabel, 'CONTINUING…', 'CONTINUE TO PAYMENT', true);

      const sampleBusinessFields = collectFormFields(form);
      // Cached regardless of outcome below — she typed real business
      // details either way, so the application form can reuse them.
      saveBusinessDetails(sampleBusinessFields);

      let result;
      try {
        result = await postJson(apiBase, '/stockists/sample-request', {
          email,
          source: captureSource(),
          samples,
          business: sampleBusinessFields,
        }, { idempotencyKey: sampleIdempotencyKey || (sampleIdempotencyKey = generateIdempotencyKey()) });
      } catch (networkError) {
        setFormLoading(submitButton, submitLabel, 'CONTINUING…', 'CONTINUE TO PAYMENT', false);
        showFormMessage(errorEl, "We couldn't reach Ancarta right now. Please try again — nothing has been created yet.");
        return;
      }

      if (result.response.ok && result.data && result.data.success) {
        publish('stockist_sample_requested', {});

        if (result.data.paymentStatus === 'awaiting_payment' && result.data.checkoutUrl) {
          // Real automation available — send her straight to the real
          // Shopify-hosted payment page rather than making her wait for a
          // manual invoice email.
          showFormMessage(successEl, 'Taking you to payment…');
          window.location.href = result.data.checkoutUrl;
          return;
        }

        // Automation unavailable for this request (missing scope, a real
        // Admin API error, etc) — the request itself is still fully saved
        // server-side either way, so this is a clean, honest fallback, not
        // an error state.
        setFormLoading(submitButton, submitLabel, 'CONTINUING…', 'CONTINUE TO PAYMENT', false);
        showFormMessage(successEl, result.data.message || 'Sample request received.');
        form.reset();
        sampleIdempotencyKey = generateIdempotencyKey();
        return;
      }

      setFormLoading(submitButton, submitLabel, 'CONTINUING…', 'CONTINUE TO PAYMENT', false);
      // A definitive rejection (e.g. ineligible_samples) is a genuinely new
      // situation once she adjusts her selection — don't replay a stale
      // cached error against her next, different attempt.
      sampleIdempotencyKey = generateIdempotencyKey();

      const message = result.data && result.data.message
        ? result.data.message
        : 'Something went wrong with that request. Please try again.';
      showFormMessage(errorEl, message);
    });
  }
}

/** Splits the shared collectFormFields() result into { business, event } for the /stockists/event-support payload shape — everything except email/eventType/eventDate/estimatedAttendance/message is a business field. */
function splitEventSupportFields(form) {
  const all = collectFormFields(form);
  return {
    email: all.email || '',
    business: { businessName: all.businessName || '', contactName: all.contactName || '' },
    event: {
      eventType: all.eventType || '',
      eventDate: all.eventDate || '',
      estimatedAttendance: all.estimatedAttendance || '',
      message: all.message || '',
    },
  };
}

function initEventSupportForm(apiBase) {
  const openButton = document.querySelector('[data-ref="eventSupportOpenButton"]');
  const form = document.querySelector('[data-ref="eventSupportForm"]');
  if (!openButton || !form) return;

  const emailField = form.querySelector('[data-field="email"]');

  openButton.addEventListener('click', () => {
    form.hidden = false;
    openButton.hidden = true;
    // The email gate already has her business email — prefill it here so
    // she isn't asked to type the same address a third time on this page.
    const savedEmail = getSavedEmail();
    if (emailField && savedEmail && !emailField.value) emailField.value = savedEmail;
  });

  const submitButton = form.querySelector('[data-ref="eventSupportSubmit"]');
  const submitLabel = form.querySelector('[data-ref="eventSupportSubmitLabel"]');
  const errorEl = form.querySelector('[data-ref="eventSupportError"]');
  const successEl = form.querySelector('[data-ref="eventSupportSuccess"]');
  const idleLabel = 'JOIN THE EVENT SUPPORT LIST';

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    hideFormMessage(errorEl);
    hideFormMessage(successEl);

    if (!apiBase) return;
    const fields = splitEventSupportFields(form);
    if (!fields.email) return;

    setFormLoading(submitButton, submitLabel, 'SENDING…', idleLabel, true);

    let result;
    try {
      result = await postJson(apiBase, '/stockists/event-support', {
        email: fields.email,
        source: captureSource(),
        business: fields.business,
        event: fields.event,
      });
    } catch (networkError) {
      setFormLoading(submitButton, submitLabel, 'SENDING…', idleLabel, false);
      showFormMessage(errorEl, "We couldn't reach Ancarta right now. Please try again.");
      return;
    }

    setFormLoading(submitButton, submitLabel, 'SENDING…', idleLabel, false);

    if (result.response.ok && result.data && result.data.success) {
      showFormMessage(successEl, result.data.message || "You're on the Event Support list.");
      form.reset();
      publish('stockist_event_support_requested', {});
      return;
    }

    showFormMessage(errorEl, 'Something went wrong with that request. Please try again.');
  });
}

function initApplicationForm(apiBase) {
  const form = document.querySelector('[data-ref="applicationForm"]');
  if (!form) return;

  const submitButton = form.querySelector('[data-ref="applicationSubmit"]');
  const submitLabel = form.querySelector('[data-ref="applicationSubmitLabel"]');
  const errorEl = form.querySelector('[data-ref="applicationError"]');
  const successEl = form.querySelector('[data-ref="applicationSuccess"]');

  // Reuse whatever she already told the sample form (if she filled it in)
  // rather than asking for the same business details a second time.
  const knownDetails = getBusinessDetails();
  Object.entries(knownDetails).forEach(([field, value]) => {
    if (!value) return;
    const input = form.querySelector('[data-field="' + field + '"]');
    if (input && !input.value) input.value = value;
  });

  let started = false;
  form.querySelectorAll('input, textarea').forEach((input) => {
    input.addEventListener('input', () => {
      if (!started) {
        started = true;
        publish('stockist_application_started', {});
      }
    });
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    hideFormMessage(errorEl);
    hideFormMessage(successEl);

    const email = getSavedEmail();
    if (!email || !apiBase) return;

    setFormLoading(submitButton, submitLabel, 'SENDING…', 'APPLY TO STOCK ANCARTA', true);

    let result;
    try {
      result = await postJson(apiBase, '/stockists/application', {
        email,
        source: captureSource(),
        business: collectFormFields(form),
      }, { idempotencyKey: generateIdempotencyKey() });
    } catch (networkError) {
      setFormLoading(submitButton, submitLabel, 'SENDING…', 'APPLY TO STOCK ANCARTA', false);
      showFormMessage(errorEl, "We couldn't reach Ancarta right now. Please try again.");
      return;
    }

    setFormLoading(submitButton, submitLabel, 'SENDING…', 'APPLY TO STOCK ANCARTA', false);

    if (result.response.ok && result.data && result.data.success) {
      showFormMessage(successEl, result.data.message || 'Application received.');
      form.reset();
      publish('stockist_application_submitted', {});
      return;
    }

    const message = result.data && result.data.message
      ? result.data.message
      : 'Something went wrong with that submission. Please try again.';
    showFormMessage(errorEl, message);
  });
}

/** TRY ANCARTA (TRYSTOCKIST40) — a real link to the normal storefront, not a form; nothing to submit, just a PII-free click event. */
function initTryAncartaTracking() {
  const link = document.querySelector('[data-ref="tryAncartaLink"]');
  if (!link) return;
  link.addEventListener('click', () => publish('stockist_try_ancarta_clicked', {}));
}

function init() {
  const gate = document.querySelector('.stk-gate');
  const apiBase = gate ? gate.getAttribute('data-api-base') || '' : '';

  initGate();
  initPricingViewedTracking(apiBase);
  initStartingEditTracking();
  initTryAncartaTracking();
  initSamplePicker(apiBase);
  initEventSupportForm(apiBase);
  initApplicationForm(apiBase);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
